import React from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { motion } from "framer-motion";

export default function CarruselProductos({ productos }) {
  const navigate = useNavigate();
  const carruselRef = React.useRef(null);
  const [showControls, setShowControls] = React.useState(false);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const productosLocal = JSON.parse(localStorage.getItem("productos") || "[]");
  const skusDisponibles = new Set(productosLocal.map((p) => p.sku));

  const checkScrollPosition = () => {
    if (carruselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carruselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scroll = (direction) => {
    if (carruselRef.current) {
      const { scrollLeft, clientWidth } = carruselRef.current;
      const scrollTo = direction === 'left' 
        ? scrollLeft - clientWidth * 0.7 
        : scrollLeft + clientWidth * 0.7;
      
      carruselRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  React.useEffect(() => {
    const carrusel = carruselRef.current;
    if (carrusel) {
      carrusel.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
    }
    return () => {
      if (carrusel) {
        carrusel.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [productos]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Controles de navegación */}
      {showControls && canScrollLeft && (
        <button 
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-all"
          aria-label="Anterior"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}
  
      <div 
        ref={carruselRef}
        className="overflow-x-auto whitespace-nowrap flex gap-6 pb-6 scrollbar-hide snap-x snap-mandatory"
        style={{ scrollBehavior: 'smooth' }}
      >
        {productos.map((p) => (
          <motion.div
            key={p.sku}
            className="inline-block w-64 snap-start flex-shrink-0"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <div 
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer h-full flex flex-col"
              onClick={() => {
                navigate(`/producto/${encodeURIComponent(p.sku)}`);
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }, 50);
              }}
            >
              <div className="w-full h-48 bg-white flex items-center justify-center p-4 relative overflow-hidden">
                <img
                  src={p.imagen || "https://via.placeholder.com/300"}
                  alt={p.nombre}
                  className="max-h-full max-w-full object-contain transition-transform duration-300 hover:scale-110"
                  loading="lazy"
                />
                {!skusDisponibles.has(p.sku) && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    Agotado
                  </div>
                )}
              </div>
  
              <div className="p-4 flex-1 flex flex-col bg-gray-50">
                <h3 className="text-base font-medium text-gray-900 mb-1 line-clamp-2">
                  {p.nombre}
                </h3>
                <div className="mt-auto pt-2">
                  <span className="text-sm font-semibold text-gray-500">
                    {p.categoria}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
  
      {showControls && canScrollRight && (
        <button 
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-all"
          aria-label="Siguiente"
        >
          <FiArrowRight className="w-5 h-5 text-gray-700" />
        </button>
      )}
  
      {/* Indicadores de scroll (solo móvil) */}
      <div className="md:hidden flex justify-center gap-1 mt-4">
        {productos.slice(0, 5).map((_, i) => (
          <div 
            key={i}
            className="w-2 h-2 rounded-full bg-gray-300"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}
